import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Avatar,
  Chip,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';
import Card from '../components/Card';

interface ProfileFormData {
  username: string;
  email: string;
}

const schema = yup.object({
  username: yup.string().required('Nome de usuário é obrigatório'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
});

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      email: '',
    },
  });

  // Update form values when user data becomes available
  useEffect(() => {
    if (user) {
      reset({
        username: user.username || '',
        email: user.email || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.put('/auth/profile', data);
      updateUser(response.data.user);
      setSuccess('Perfil atualizado com sucesso!');
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Meu Perfil
            </Typography>
          </Box>

          {/* Profile Card */}
          <Card sx={{ p: 4 }}>
            <Stack spacing={4}>
              {/* User Avatar & Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {user.username}
                  </Typography>
                  <Chip 
                    label={user.role === 'admin' ? 'Administrador' : 'Funcionário'}
                    color={user.role === 'admin' ? 'primary' : 'secondary'}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              {/* Alerts */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nome de Usuário"
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        disabled={loading}
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={loading}
                      />
                    )}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    sx={{
                      height: 48,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    {loading ? 'Salvando...' : 'Salvar Alterações'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
} 